import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Globe, ShieldCheck, BarChart, Users, Briefcase, BrainCircuit, Target, CheckCircle, Lightbulb, BookOpen, FileText, ExternalLink, Cpu, Database, LineChart, Cog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SEOHead from "@/components/seo-head";
import heroPortrait from "@assets/hero-portrait.webp";

function useReveal() {
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (node) {
      containerRef.current = node;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              (entry.target as HTMLElement).classList.add("revealed");
              observerRef.current?.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15 }
      );
      node.querySelectorAll<HTMLElement>(".reveal").forEach((el) => {
        observerRef.current!.observe(el);
      });
    }
  }, []);

  return setRef;
}

export default function Home() {
  const { t } = useTranslation();
  const revealRef = useReveal();

  const stats = [
    { value: "25+", label: "stats.experience" },
    { value: "39+", label: "stats.companies" },
    { value: "21+", label: "stats.countries" },
    { value: "95%", label: "stats.success" },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Simon Lee & Associates, LLC",
    "url": "https://simonleeandassociates.com",
    "logo": "https://simonleeandassociates.com/assets/logo-full.png",
    "description": "Boutique healthcare consulting firm helping HealthTech, Biotech, and MedTech companies navigate global market entry with 25+ years of industry expertise.",
    "telephone": "+1-314-562-2900",
    "email": "simon@simonleeandassociates.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Saint Johns",
      "addressRegion": "FL",
      "addressCountry": "US"
    },
    "sameAs": [
      "https://www.linkedin.com/company/108036011"
    ],
    "knowsAbout": [
      "HealthTech consulting",
      "Biotech market entry",
      "MedTech regulatory strategy",
      "Global healthcare market expansion"
    ]
  };

  return (
    <>
      <SEOHead
        title="Simon Lee & Associates | Healthcare Consulting for HealthTech, Biotech & MedTech"
        description="Boutique consulting firm with 25+ years of experience guiding healthcare companies through global market entry. Strategic advisory, regulatory navigation, and business development across 21+ countries."
        canonical="https://simonleeandassociates.com"
        keywords="healthcare consulting, HealthTech consulting firm, Biotech market entry, MedTech advisory, global healthcare consulting, US market entry strategy, healthcare business development, medical device consulting, life sciences consulting, FDA regulatory consulting, Healthcare AI, Health AI, Medical AI"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    <div className="overflow-hidden" ref={revealRef}>

      <section id="hero" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden bg-background">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
          <div className="absolute -top-[20%] -left-[10%] w-[50vw] h-[50vw] bg-primary/[0.14] dark:bg-primary/20 rounded-full blur-[100px] animate-[heroGlow1_20s_linear_infinite] will-change-transform" />
          <div className="absolute bottom-[10%] left-[20%] w-[30vw] h-[30vw] bg-blue-500/10 dark:bg-blue-500/15 rounded-full blur-[80px] animate-[heroGlow2_25s_linear_infinite] will-change-transform" />
          <div className="absolute top-[10%] right-[10%] w-[40vw] h-[40vw] bg-primary/10 dark:bg-primary/15 rounded-full blur-[90px] animate-[heroGlow3_18s_linear_infinite] will-change-transform" />
        </div>

        <div className="absolute inset-0 pointer-events-none overflow-hidden z-[15]">
          <div className="absolute border border-primary/[0.17] dark:border-primary/25 rounded-lg will-change-transform animate-[heroShape_15s_ease-in-out_infinite]" style={{ top: "12%", left: "8%", width: 80, height: 80 }} />
          <div className="absolute border border-primary/[0.17] dark:border-primary/25 rounded-lg will-change-transform animate-[heroShape_20s_ease-in-out_2s_infinite]" style={{ top: "65%", left: "5%", width: 60, height: 60 }} />
          <div className="absolute border border-primary/[0.17] dark:border-primary/25 rounded-lg will-change-transform animate-[heroShape_12s_ease-in-out_4s_infinite]" style={{ top: "20%", left: "45%", width: 40, height: 40 }} />
          <div className="absolute rounded-full bg-primary/20 dark:bg-primary/30 will-change-transform animate-[heroDot_4s_ease-in-out_infinite]" style={{ top: "18%", left: "25%", width: 14, height: 14 }} />
          <div className="absolute rounded-full bg-primary/20 dark:bg-primary/30 will-change-transform animate-[heroDot_4s_ease-in-out_1.5s_infinite]" style={{ top: "55%", left: "15%", width: 10, height: 10 }} />
          <div className="absolute rounded-full bg-primary/20 dark:bg-primary/30 will-change-transform animate-[heroDot_4s_ease-in-out_3s_infinite]" style={{ top: "30%", left: "70%", width: 12, height: 12 }} />
        </div>

        <div className="absolute inset-0 z-0 select-none pointer-events-none overflow-hidden">
          <div className="absolute top-0 bottom-0 ltr:right-0 ltr:left-auto rtl:left-0 rtl:right-auto flex items-end">
            <img
              src={heroPortrait}
              alt="Simon Lee - Founder of Simon Lee & Associates, healthcare consulting expert"
              width={575}
              height={1024}
              loading="eager"
              fetchPriority="high"
              className="h-[110%] lg:h-[120%] w-auto object-contain object-bottom grayscale opacity-30 dark:opacity-50 lg:opacity-50 dark:lg:opacity-60 ltr:translate-x-[10%] rtl:translate-x-[-10%] animate-[heroFadeIn_1.5s_ease-out_forwards]"
              style={{ opacity: 0 }}
            />
          </div>
          <div className="absolute inset-0 ltr:bg-gradient-to-r rtl:bg-gradient-to-l from-background via-background/85 dark:via-background/80 via-60% to-transparent z-10" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent z-10" />
          <div className="absolute ltr:right-0 rtl:left-0 top-0 bottom-0 w-32 ltr:bg-gradient-to-l rtl:bg-gradient-to-r from-background/50 to-transparent z-10" />
        </div>

        <div className="container px-4 grid lg:grid-cols-2 gap-12 items-center relative z-20">
          <div className="max-w-2xl">
            <div
              className="inline-flex items-center gap-3 px-4 sm:px-6 py-2.5 rounded-full bg-primary/10 text-primary text-base sm:text-lg md:text-xl font-medium mb-12 backdrop-blur-sm border border-primary/20 max-w-full animate-[fadeInUp_0.6s_ease-out_0.2s_both]"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-primary animate-[heroPing_1.5s_cubic-bezier(0,0,0.2,1)_infinite] will-change-transform"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
              </span>
              {t("hero.badge")}
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-8 md:mb-12 text-foreground leading-tight">
              <span className="block md:whitespace-nowrap animate-[fadeInUp_0.5s_ease-out_0.3s_both]">
                {t("hero.line1")}
              </span>
              <span className="block md:whitespace-nowrap animate-[fadeInUp_0.5s_ease-out_0.5s_both]">
                {t("hero.line2")}
              </span>
            </h1>

            <p
              className="text-xl md:text-3xl text-muted-foreground mb-10 md:mb-14 max-w-3xl leading-relaxed animate-[fadeIn_0.8s_ease-out_1s_both]"
            >
              {t("hero.description")}
            </p>

            <div
              className="flex flex-col sm:flex-row gap-5 md:gap-8 animate-[fadeInUp_0.6s_ease-out_1.2s_both]"
            >
              <a href="/contact">
                <Button size="lg" className="text-base sm:text-lg md:text-xl px-6 sm:px-8 md:px-12 h-auto py-3.5 md:py-4 group whitespace-normal text-center" data-testid="button-hero-cta">
                  <span>{t("hero.cta")}</span>
                  <ArrowRight className="ml-2 h-5 w-5 md:h-6 md:w-6 shrink-0 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <a href="/services">
                <Button variant="outline" size="lg" className="text-base sm:text-lg md:text-xl px-6 sm:px-8 md:px-12 h-auto py-3.5 md:py-4 bg-background/50 backdrop-blur-sm whitespace-normal text-center" data-testid="button-hero-services">
                  <span>{t("nav.services")}</span>
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-muted/50 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="reveal text-center"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2" data-testid={`stat-value-${index}`}>{stat.value}</div>
                <div className="text-sm md:text-base text-muted-foreground font-medium uppercase tracking-wide">{t(stat.label)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" aria-label="About Simon Lee & Associates">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <div className="reveal">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-about-title">{t("about.title")}</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">{t("about.desc1")}</p>
            <a href="/about">
              <Button size="lg" className="text-base sm:text-lg px-8 h-12 group" data-testid="button-cta-about">
                <span>{t("cta.learnHow")}</span>
                <ArrowRight className="ml-2 h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/20" aria-label="Why Choose Us">
        <div className="container mx-auto px-4">
          <div className="reveal text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-whyus-title">{t("whyUs.title")}</h2>
            <p className="text-lg text-muted-foreground">{t("whyUs.subtitle")}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto mb-10">
            {[
              { icon: Target, key: "expertise" },
              { icon: CheckCircle, key: "trackRecord" },
              { icon: Users, key: "network" },
              { icon: Lightbulb, key: "approach" },
            ].map((item, i) => (
              <div
                key={i}
                className="reveal text-center p-4 rounded-xl bg-background border border-border/50"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="mx-auto w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-sm md:text-base font-semibold" data-testid={`text-whyus-${item.key}`}>{t(`whyUs.${item.key}`)}</h3>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a href="/why-sla">
              <Button size="lg" className="text-base sm:text-lg px-8 h-12 group" data-testid="button-cta-why-us">
                <span>{t("cta.whyChoose")}</span>
                <ArrowRight className="ml-2 h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/30" aria-label="Our Services">
        <div className="container mx-auto px-4">
          <div className="reveal text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-services-title">{t("services.title")}</h2>
            <p className="text-lg text-muted-foreground">{t("services.subtitle")}</p>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 md:gap-4 max-w-4xl mx-auto mb-10">
            {[
              { key: "marketEntry", icon: Globe, color: "text-blue-500" },
              { key: "regulatory", icon: ShieldCheck, color: "text-green-500" },
              { key: "bizDev", icon: BarChart, color: "text-purple-500" },
              { key: "partnerships", icon: Users, color: "text-orange-500" },
              { key: "advisory", icon: Briefcase, color: "text-red-500" },
              { key: "innovation", icon: BrainCircuit, color: "text-teal-500" },
              { key: "digitalStrategy", icon: Cpu, color: "text-cyan-500" },
              { key: "dataGov", icon: Database, color: "text-amber-500" },
              { key: "analytics", icon: LineChart, color: "text-indigo-500" },
              { key: "automation", icon: Cog, color: "text-emerald-500" },
            ].map((service, i) => (
              <div
                key={service.key}
                className="reveal text-center p-3 md:p-4 rounded-xl bg-card border border-border/50"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div className={`mx-auto w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-2 ${service.color}`}>
                  <service.icon className="h-5 w-5" />
                </div>
                <h3 className="text-xs md:text-sm font-semibold leading-tight">{t(`services.${service.key}`)}</h3>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a href="/services">
              <Button size="lg" className="text-base sm:text-lg px-8 h-12 group" data-testid="button-cta-services">
                <span>{t("cta.exploreServices")}</span>
                <ArrowRight className="ml-2 h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" aria-label="Our Approach">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <div className="reveal">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-process-title">{t("process.title")}</h2>
            <p className="text-lg text-muted-foreground mb-8">{t("process.subtitle")}</p>
            <a href="/approach">
              <Button size="lg" className="text-base sm:text-lg px-8 h-12 group" data-testid="button-cta-process">
                <span>{t("cta.startJourney")}</span>
                <ArrowRight className="ml-2 h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/20" aria-label="Success Stories">
        <div className="container mx-auto px-4">
          <div className="reveal text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-success-title">{t("nav.success")}</h2>
            <p className="text-lg text-muted-foreground">{t("success.subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
            {[
              { title: t("success.story1Title"), tags: t("success.story1Tags").split(", ") },
              { title: t("success.story2Title"), tags: t("success.story2Tags").split(", ") },
              { title: t("success.story3Title"), tags: t("success.story3Tags").split(", ") },
            ].map((story, i) => (
              <div
                key={i}
                className="reveal"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <Card className="h-full hover:border-primary/50 transition-colors shimmer-hover" data-testid={`card-success-${i}`}>
                  <CardContent className="pt-6">
                    <h3 className="text-lg font-bold mb-3">{story.title}</h3>
                    <div className="flex gap-2 flex-wrap">
                      {story.tags.map(tag => (
                        <span key={tag} className="text-xs px-2 py-1 bg-muted rounded-md font-medium text-muted-foreground">{tag}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a href="/success-stories">
              <Button size="lg" className="text-base sm:text-lg px-8 h-12 group" data-testid="button-cta-success">
                <span>{t("cta.discussProject")}</span>
                <ArrowRight className="ml-2 h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" aria-label="Our Team">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <div className="reveal">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-team-title">{t("team.title")}</h2>
            <p className="text-lg text-muted-foreground mb-8">{t("team.subtitle")}</p>
            <a href="/team">
              <Button size="lg" className="text-base sm:text-lg px-8 h-12 group" data-testid="button-cta-team">
                <span>{t("cta.meetTeam")}</span>
                <ArrowRight className="ml-2 h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/20" aria-label="Research and Insights">
        <div className="container mx-auto px-4">
          <div className="reveal text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="text-insights-title">{t("insights.title")}</h2>
            <p className="text-lg text-muted-foreground">{t("insights.subtitle")}</p>
          </div>
          <div className="max-w-2xl mx-auto mb-10">
            <div
              className="reveal group bg-card border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 shimmer-hover"
              data-testid="card-insight-1"
            >
              <div className="p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                    <BookOpen className="h-3.5 w-3.5" />
                    {t("insights.research")}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-500/10 text-orange-600 dark:text-orange-400 text-sm font-medium rounded-full">
                    <FileText className="h-3.5 w-3.5" />
                    {t("insights.pdfAvailable")}
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  {t("insights.article1Title")}
                </h3>
                <p className="text-muted-foreground text-base leading-relaxed mb-5">
                  {t("insights.article1Desc")}
                </p>
                <a href="/reports/snf_payer_mix_report_2024_2025.pdf" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="group/btn" data-testid="button-read-more-1">
                    {t("insights.downloadReport")}
                    <ExternalLink className="ml-1.5 h-3.5 w-3.5 transition-transform group-hover/btn:translate-x-0.5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
          <div className="text-center">
            <a href="/insights">
              <Button variant="outline" size="lg" className="text-base sm:text-lg px-8 h-12" data-testid="button-view-all-insights">
                <span>{t("insights.viewAll")}</span>
                <ArrowRight className="ml-2 h-5 w-5 shrink-0" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary text-primary-foreground relative overflow-hidden" aria-label="Contact Us">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
          <div className="reveal">
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-testid="text-contact-title">{t("contact.title")}</h2>
            <p className="text-xl text-primary-foreground/80 mb-10 max-w-xl mx-auto">{t("contact.subtitle")}</p>
            <a href="/contact">
              <Button size="lg" className="text-base sm:text-lg md:text-xl px-8 md:px-12 h-auto py-3.5 md:py-4 bg-primary-foreground text-primary hover:bg-primary-foreground/90 group" data-testid="button-cta-contact">
                <span>{t("hero.cta")}</span>
                <ArrowRight className="ml-2 h-5 w-5 md:h-6 md:w-6 shrink-0 transition-transform group-hover:translate-x-1" />
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
